import React from "react";
import { View, StyleSheet, ScrollView } from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import CustomText from "../../components/common/general/CustomText/CustomText";
import { Moon } from "lucide-react";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import Pill from "../../components/common/Pill/Pill";
import { StudentNudgePreferenceDetails } from "../../graphql/generated/graphql";

interface NudgeManagementTableProps {
  preferences: StudentNudgePreferenceDetails[];
}

const NudgeManagementTable: React.FC<NudgeManagementTableProps> = ({ preferences }) => {
  if (preferences.length === 0) {
    return (
      <View style={styles.emptyContainer}>
        <CustomText
          text="No students found"
          size="m"
          weight="bold"
          style={{ color: "#101828" }}
          useTranslationText={false}
        />
        <CustomSpacing type="vertical" size="xs" />
        <CustomText
          text="No students are currently enrolled in your courses."
          size="s"
          style={{ color: "#667085" }}
          useTranslationText={false}
        />
      </View>
    );
  }

  const formatTimeAgo = (dateStr: string | null | undefined) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();

    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffSecs / 60);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffSecs < 60) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return `Yesterday`;
    return `${diffDays}d ago`;
  };

  return (
    <ScrollView style={styles.tableContainer} tabIndex={0}>
      <View style={styles.table}>
        <View style={styles.tableHeader}>
          <View style={[styles.cell, styles.nameCell]}>
            <CustomText text="STUDENT" size="xs" weight="bold" style={styles.headerText} useTranslationText={false} />
          </View>
          <View style={[styles.cell, styles.statusCell]}>
            <CustomText
              text="SMS STATUS"
              size="xs"
              weight="bold"
              style={styles.headerText}
              useTranslationText={false}
            />
          </View>
          <View style={[styles.cell, styles.freqCell]}>
            <CustomText text="FREQUENCY" size="xs" weight="bold" style={styles.headerText} useTranslationText={false} />
          </View>
          <View style={[styles.cell, styles.timesCell]}>
            <CustomText
              text="PREFERRED TIMES"
              size="xs"
              weight="bold"
              style={styles.headerText}
              useTranslationText={false}
            />
          </View>
          <View style={[styles.cell, styles.nudgesCell]}>
            <CustomText
              text="NUDGES SENT"
              size="xs"
              weight="bold"
              style={styles.headerText}
              useTranslationText={false}
            />
          </View>
          <View style={[styles.cell, styles.lastCell]}>
            <CustomText
              text="LAST NUDGE"
              size="xs"
              weight="bold"
              style={styles.headerText}
              useTranslationText={false}
            />
          </View>
        </View>

        {preferences.map((pref) => {
          const name = pref.name || "Student User";
          const email = pref.email;
          const initials = name
            .split(" ")
            .map((n) => n[0])
            .join("")
            .toUpperCase()
            .substring(0, 2);

          const isSmsEnabled = !!pref.smsEnabled;
          const hasSmsConsent = !!pref.smsConsent;
          const freqText = pref.frequency || "DAILY";

          return (
            <View key={pref.userId} style={styles.tableRow}>
              <View style={[styles.cell, styles.nameCell]}>
                <View style={styles.nameContent}>
                  <LinearGradient
                    colors={["#E6EBFF", "#E6EBFF"]}
                    style={styles.avatar}
                    start={{ x: 0, y: 0.5 }}
                    end={{ x: 1, y: 0.5 }}>
                    <CustomText
                      text={initials}
                      size="s"
                      weight="medium"
                      useTranslationText={false}
                      style={styles.avatarText}
                    />
                  </LinearGradient>
                  <View style={styles.nameInfo}>
                    <CustomText text={name} size="s" weight="bold" useTranslationText={false} style={styles.nameText} />
                    {email && email.includes("@") ? (
                      <CustomText text={email} size="xs" style={styles.emailText} useTranslationText={false} />
                    ) : null}
                    {pref.snoozeAllUntil && new Date(pref.snoozeAllUntil) > new Date() && (
                      <View style={styles.snoozeWrapper}>
                        <Moon size={12} color="#B45309" />
                        <CustomText
                          text={`Snoozed until ${new Date(pref.snoozeAllUntil).toLocaleDateString("en-US", { month: "short", day: "numeric" })}`}
                          size="xs"
                          style={styles.snoozeText}
                          useTranslationText={false}
                        />
                      </View>
                    )}
                  </View>
                </View>
              </View>

              <View
                style={[
                  styles.cell,
                  styles.statusCell,
                  { flexDirection: "row", gap: 6, flexWrap: "wrap", justifyContent: "flex-start" },
                ]}>
                {hasSmsConsent ? (
                  <Pill
                    text="Consented"
                    textSize="xs"
                    backgroundColor="#F0FDF4"
                    textColor="#16A34A"
                    borderColor="#86EFAC"
                    leftIcon={null}
                  />
                ) : (
                  <Pill
                    text="No Consent"
                    textSize="xs"
                    backgroundColor="#F1F5F9"
                    textColor="#475569"
                    borderColor="#CBD5E1"
                    leftIcon={null}
                  />
                )}
                {isSmsEnabled && (
                  <Pill
                    text="Enabled"
                    textSize="xs"
                    backgroundColor="#F0FDF4"
                    textColor="#16A34A"
                    borderColor="#86EFAC"
                    leftIcon={null}
                  />
                )}
              </View>

              <View style={[styles.cell, styles.freqCell]}>
                <Pill text={freqText} backgroundColor="#EEF2FF" textColor="#4338CA" textSize="xs" leftIcon={null} />
              </View>

              <View
                style={[
                  styles.cell,
                  styles.timesCell,
                  { flexDirection: "row", gap: 6, flexWrap: "wrap", alignItems: "center" },
                ]}>
                <Pill
                  text={pref.preferredSendTime || "08:00"}
                  backgroundColor="#F1F5F9"
                  textColor="#475569"
                  leftIcon={null}
                />
              </View>

              <View style={[styles.cell, styles.nudgesCell]}>
                <CustomText
                  text={(pref.nudgesSent || 0).toString()}
                  size="s"
                  weight="bold"
                  useTranslationText={false}
                  style={{ color: "#111827" }}
                />
              </View>

              <View style={[styles.cell, styles.lastCell]}>
                <CustomText
                  text={formatTimeAgo(pref.lastNudge)}
                  size="s"
                  style={{ color: "#6B7280" }}
                  useTranslationText={false}
                />
              </View>
            </View>
          );
        })}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  tableContainer: {
    flex: 1,
    backgroundColor: "#fff",
  },
  table: {
    backgroundColor: "#fff",
    overflow: "hidden",
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingVertical: 12,
    paddingHorizontal: 24,
  },
  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    paddingVertical: 16,
    paddingHorizontal: 24,
    minHeight: 72,
    alignItems: "center",
  },
  cell: {
    justifyContent: "center",
    paddingHorizontal: 8,
  },
  nameCell: {
    flex: 2,
  },
  statusCell: {
    flex: 1.5,
  },
  freqCell: {
    flex: 1.2,
    alignItems: "flex-start",
  },
  timesCell: {
    flex: 1.2,
  },
  nudgesCell: {
    flex: 1,
    alignItems: "center",
  },
  lastCell: {
    flex: 1,
    alignItems: "flex-end",
  },
  headerText: {
    color: "#667085",
    letterSpacing: 0.5,
    fontSize: 11,
  },
  nameContent: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    justifyContent: "center",
    alignItems: "center",
  },
  avatarText: {
    color: "#012298",
    fontSize: 14,
  },
  nameInfo: {
    flex: 1,
  },
  nameText: {
    color: "#101828",
    fontSize: 14,
  },
  emailText: {
    color: "#667085",
    marginTop: 2,
    fontSize: 12,
  },
  snoozeWrapper: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    marginTop: 4,
  },
  snoozeText: {
    color: "#B45309",
    fontWeight: "500",
    fontSize: 12,
  },
  emptyContainer: {
    flex: 1,
    minHeight: 300,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#fff",
    padding: 32,
  },
});

export default NudgeManagementTable;
